import { Building2, Coins, Wallet } from 'lucide-react';
import { getEmbeddedSubPaymentMethods } from './subPaymentMethods';
import { resolveWalletIconUrl } from './walletIcons';

export const MIN_WITHDRAWAL_USD = 50;

export const extractWalletsList = (response) => {
  if (!response?.success) return [];
  const payload = response.data;
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload?.result)) return payload.result.filter(Boolean);
  if (Array.isArray(payload?.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload?.wallets)) return payload.wallets.filter(Boolean);
  if (Array.isArray(payload?.items)) return payload.items.filter(Boolean);
  if (Array.isArray(payload?.records)) return payload.records.filter(Boolean);
  return [];
};

export const resolveMethodKeyFromName = (name = '') => {
  const type = name.toString().toLowerCase();
  if (/local bank|bank transfer|wire transfer|\bbank\b/i.test(type)) return 'bank';
  if (/trc20|trc-20/i.test(type)) return 'usdt_trc20';
  if (/erc20|erc-20/i.test(type)) return 'usdt_erc20';
  if (/crypto|usdt|binance|pay|wallet/i.test(type)) return 'crypto';
  return 'other';
};

const resolveMethodKey = (wallet) => {
  const type = [
    wallet.walletType,
    wallet.accountType,
    wallet.type,
    wallet.walletName,
    wallet.name,
    wallet.title,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return resolveMethodKeyFromName(type);
};

const CRYPTO_METHOD_KEYS = new Set(['usdt_trc20', 'usdt_erc20', 'crypto']);

export const isCryptoWallet = (wallet) => {
  if (!wallet) return false;
  const key = wallet.methodKey ?? resolveMethodKey(wallet.raw || wallet);
  if (CRYPTO_METHOD_KEYS.has(key)) return true;
  const name = [wallet.title, wallet.raw?.walletName, wallet.walletName, wallet.name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return /crypto|usdt|trc-?20|erc-?20|binance pay/i.test(name);
};

export const isBankWallet = (wallet) => wallet?.methodKey === 'bank';

export const walletNeedsSubPaymentMethods = (wallet) => {
  if (!wallet) return false;
  if (wallet.hasSubPaymentMethods) return true;
  return !isBankWallet(wallet);
};

export const isLocalBankWallet = (wallet) => {
  if (!wallet) return false;
  if (wallet.methodKey === 'bank') return true;
  const name = (wallet.title || wallet.raw?.walletName || '').toLowerCase();
  return /local bank|bank transfer|wire transfer/i.test(name);
};

export const getWalletLimits = (wallet, availableBalance = Infinity) => {
  const walletMin = Number(wallet?.minWithdrawal ?? wallet?.raw?.minWithdrawal) || 0;
  const min = Math.max(MIN_WITHDRAWAL_USD, walletMin);
  const maxFromWallet = Number(wallet?.maxWithdrawal ?? wallet?.raw?.maxWithdrawal);
  const maxWallet = Number.isFinite(maxFromWallet) && maxFromWallet > 0 ? maxFromWallet : Infinity;
  const max = Math.min(maxWallet, availableBalance);
  return { min, max: Math.max(min, max) };
};

export const getWithdrawalRate = (wallet) => {
  const rate = Number(wallet?.withdrawalRate ?? wallet?.raw?.withdrawalRate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
};

export const getConvertedAmount = (amount, wallet) => {
  const num = Number(amount) || 0;
  if (!isLocalBankWallet(wallet)) return num;
  return num * getWithdrawalRate(wallet);
};

const formatLimit = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

export const mapWalletToMethod = (wallet, index = 0) => {
  const methodKey = resolveMethodKey(wallet);
  const title = wallet.walletName || wallet.name || wallet.title || 'Withdrawal Method';
  const minWithdrawal = Math.max(MIN_WITHDRAWAL_USD, Number(wallet.minWithdrawal) || 0);
  const maxWithdrawal = Number(wallet.maxWithdrawal) || 0;
  const withdrawalRate = getWithdrawalRate({ withdrawalRate: wallet.withdrawalRate });

  const limitParts = [];
  if (minWithdrawal > 0) limitParts.push(`Min ${formatLimit(minWithdrawal)}`);
  if (maxWithdrawal > 0) limitParts.push(`Max ${formatLimit(maxWithdrawal)}`);
  const subtitle =
    limitParts.join(' · ') ||
    wallet.description ||
    wallet.subTitle ||
    wallet.processingTime ||
    '';

  const disabled =
    wallet.isDisabled === true ||
    wallet.disabled === true ||
    wallet.isActive === false ||
    wallet.status?.toString().toLowerCase() === 'inactive';

  const embeddedSubMethods = getEmbeddedSubPaymentMethods({ raw: wallet, subPaymentMethods: wallet.subPaymentMethods });
  const hasSubPaymentMethods =
    wallet.hasSubPaymentMethods === true ||
    wallet.isSubPaymentRequired === true ||
    embeddedSubMethods.length > 0;

  return {
    id: String(wallet.walletId ?? wallet.id ?? wallet.affiliateWalletId ?? index),
    walletId: wallet.walletId ?? wallet.id ?? null,
    methodKey,
    title,
    subtitle,
    disabled,
    withdrawalRate,
    minWithdrawal,
    maxWithdrawal,
    hasSubPaymentMethods,
    subPaymentMethods: embeddedSubMethods,
    iconUrl: resolveWalletIconUrl({ title }),
    raw: wallet,
    accountHolder: wallet.accountTitle ?? wallet.accountHolder ?? wallet.accountName ?? '',
    accountNumber: wallet.accountNo ?? wallet.accountNumber ?? '',
    bankName: wallet.accountBank ?? wallet.bankName ?? '',
    swiftCode: wallet.accountIBAN ?? wallet.swiftCode ?? wallet.iban ?? '',
    cryptoAddress: wallet.walletAddress ?? wallet.cryptoAddress ?? wallet.accountNo ?? '',
    accountType: wallet.accountType ?? wallet.walletType ?? '',
  };
};

export const getWalletIcon = (methodKey) => {
  if (methodKey === 'bank') return Building2;
  if (methodKey === 'usdt_trc20' || methodKey === 'usdt_erc20' || methodKey === 'crypto') return Coins;
  return Wallet;
};

export const walletToFormState = (wallet) => ({
  accountHolder: wallet?.accountHolder || '',
  accountNumber: wallet?.accountNumber || '',
  bankName: wallet?.bankName || '',
  swiftCode: wallet?.swiftCode || '',
});
